import * as React from 'react'

import { styled } from '~/styled'
import { Override } from '~/utilities/types'

const StyledSelect = styled(
  'select',
  {
    base: [
      'bg-(image:--bg-chevron)',
      'appearance-none',
      '[background-repeat:no-repeat,repeat-x]',
      'rounded-md',
      'text-grey-1000',
      'block',
      'font-body',
      'font-normal',
      'leading-[1.4]',
      'w-full',
      'hover:cursor-pointer',
      '-ms-expand:hidden',
      'disabled:opacity-30',
      'disabled:cursor-not-allowed',
      '*[[disabled]]:opacity-30',
      '*[[disabled]]:cursor-not-allowed'
    ],
    variants: {
      size: {
        sm: [
          'bg-position-[right_--spacing(2)_top_50%,0_0]',
          'bg-size-[18px_auto,100%]',
          'text-sm',
          'h-8',
          'pl-2',
          'pr-8'
        ],
        md: [
          'bg-position-[right_--spacing(3)_top_50%,0_0]',
          'bg-size-[20px_auto,100%]',
          'text-md',
          'h-10',
          'pl-3',
          'pr-10'
        ],
        lg: [
          'bg-position-[right_--spacing(3)_top_50%,0_0]',
          'bg-size-[20px_auto,100%]',
          'text-md',
          'h-12',
          'pl-3',
          'pr-10'
        ]
      },
      state: {
        error: []
      },
      theme: {
        white: [],
        grey: []
      },
      appearance: {
        standard: [
          'bg-white',
          'border',
          'border-grey-700',
          'focus:border-primary-800',
          'focus:outline-none'
        ],
        modern: [
          'bg-grey-100',
          'border-none',
          'focus:outline-2',
          'focus:outline-blue-800',
          'focus:outline-offset-1',
          'focus:outline-solid',
          'focus:z-1'
        ]
      }
    },
    compoundVariants: [
      {
        state: 'error',
        appearance: 'standard',
        class: ['border-danger']
      },
      {
        state: 'error',
        appearance: 'modern',
        class: ['bg-danger-light', 'focus:outline-danger']
      },
      {
        appearance: 'modern',
        theme: 'white',
        class: ['bg-white']
      },
      {
        appearance: 'modern',
        theme: 'grey',
        class: ['bg-grey-100']
      }
    ]
  },
  { enabledResponsiveVariants: true }
)

export type SelectProps = Override<
  React.ComponentProps<typeof StyledSelect>,
  {
    as?: never
    placeholder?: string
  }
  // TODO: figure out why uncommenting this causes TS errors in
  // component declaration
  // & (
  //   | { id: string; 'aria-label'?: string }
  //   | { 'aria-label': string; id?: string }
  // )
>

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      placeholder,
      children,
      size = 'md',
      appearance = 'standard',
      ...remainingProps
    },
    ref
  ) => {
    // Type assertion needed due to responsive variant size prop compatibility
    const props: React.ComponentProps<typeof StyledSelect> = {
      size,
      appearance,
      ref,
      ...remainingProps
    }

    if (
      [remainingProps.value, remainingProps.defaultValue].every(
        (value) => value === undefined
      )
    ) {
      props.defaultValue = ''
    }

    return (
      <StyledSelect {...props}>
        {placeholder && (
          <option disabled hidden value="">
            {placeholder}
          </option>
        )}
        {children}
      </StyledSelect>
    )
  }
)

Select.displayName = 'Select'
